import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { mergeDeepWith } from 'ramda';

import { BonusStatus } from '@ezugi/constants';
import { TableChip } from '@ezugi/types';

type BonusData = {
  status: BonusStatus;
  selectedChip?: TableChip;
  multiplierPrize: number;
  wonAmount: number;
  rejected: boolean;
  visible: boolean;
  wasStreamSoundMutedBeforeBonus?: boolean;
  hasOperatorError?: boolean;
  chips: TableChip[];
  roundId?: number;
};

type BonusTimers = {
  timeLeft: number;
  duration: number;
};

type BonusState = BonusData & { timers: BonusTimers };

export type BonusRejectionActionPayload = {
  rejected: boolean;
  userCancelled?: boolean;
};

export const INITIAL_STATE: BonusState = {
  status: BonusStatus.WaitingForTrigger,
  wonAmount: 0,
  multiplierPrize: 0,
  timers: {
    timeLeft: 0,
    duration: 0,
  },
  rejected: false,
  visible: false,
  chips: [],
};

export const mergeDeepWithArrayOverwrite = (object1: any, object2: any) => mergeDeepWith((left, right) => {
  if (Array.isArray(left) && Array.isArray(right)) {
    return right;
  }

  return right;
}, object1, object2);

const bonusSlice = createSlice({
  name: 'bonus',
  initialState: INITIAL_STATE,
  reducers: {
    selectChip: (state, { payload }: PayloadAction<TableChip>) => ({
      ...state,
      selectedChip: payload,
    }),
    // Use only once on BonusMessage.bonusWheelStart to add all necessary data
    init: (state, { payload }: PayloadAction<Partial<BonusState>>) => mergeDeepWithArrayOverwrite(state, payload),
    setStatus: (state, { payload }: PayloadAction<BonusStatus>) => ({
      ...state,
      status: payload,
    }),
    setMultiplierPrize: (state, { payload }: PayloadAction<number>) => ({
      ...state,
      multiplierPrize: payload,
    }),
    setTimers: (state, { payload }: PayloadAction<Partial<BonusTimers>>) => ({
      ...state,
      timers: {
        ...state.timers,
        ...payload,
      },
    }),
    setRejected: (
      state,
      {
        payload,
      }: PayloadAction<BonusRejectionActionPayload>,
    ) => ({
      ...state,
      rejected: payload.rejected,
    }),
    setWonAmount: (state, { payload }: PayloadAction<number>) => ({
      ...state,
      wonAmount: payload,
    }),
    setVisibility: (state, { payload }: PayloadAction<boolean>) => ({
      ...state,
      visible: payload,
    }),
    setWasStreamSoundMutedBeforeBonus: (
      state,
      { payload }: PayloadAction<boolean>,
    ) => ({
      ...state,
      wasStreamSoundMutedBeforeBonus: payload,
    }),
    setHasOperatorError: (state, { payload }: PayloadAction<boolean>) => ({
      ...state,
      hasOperatorError: payload,
    }),
  },
});

export const bonusActionsFromSlice = bonusSlice.actions;
export default bonusSlice.reducer;
